import React, { useMemo } from 'react'
import { useTable, useExpanded, type Row } from 'react-table'
import './people-table.css'
import { Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import { type Person } from 'services'
import { useSessionContext } from 'session'

interface PeopleTableProps {
  people: Person[]
  orderField: string
  orderDirection: string
  updateOrdering: (field: string, direction: string) => void
}

export const PeopleTable: React.FC<PeopleTableProps> = ({ people, orderField, orderDirection, updateOrdering }) => {
  const navigate = useNavigate()
  const { sessionData, openNewTab } = useSessionContext()

  const columns: any = useMemo(
    () => [
      { Header: 'ID', accessor: 'id', orderField: 'id' },
      { Header: 'Unique ID', accessor: 'uniqueId', orderField: 'uniqueid' },
      { Header: 'First Name', accessor: 'firstName', orderField: 'first_name' },
      { Header: 'Last Name', accessor: 'lastName', orderField: 'last_name' },
      {
        Header: 'Edit',
        id: 'edit',
        Cell: ({ row }: { row: Row<Person> }) => (
                    <Button
                        variant="contained"
                        size='medium'
                        onClick={() => {
                          openNewTab(`/editPerson/${row.values.id as number}`)
                        }}
                    >
                        <ModeEditIcon />
                    </Button>
        )
      }
    ],
    [navigate, sessionData.newTab]
  )

  const tableData = useMemo(() => people, [people])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable<Person>(
    {
      columns,
      data: tableData
    },
    useExpanded
  )

  function handleHeaderClick (column: any): void {
    const direction = orderField === column.orderField ? (orderDirection === 'ASC' ? 'DESC' : 'ASC') : orderDirection
    updateOrdering(column.orderField, direction)
  }

  return (
      <React.Fragment>
          {people.length === 0
            ? (
              <p>No people to show.</p>
              )
            : (
        <table {...getTableProps()} className="table">
            <thead>
            {headerGroups.map((headerGroup) => {
              return (
                    <tr {...headerGroup.getHeaderGroupProps()} key={'header' + headerGroup.id}>
                        {headerGroup.headers.map((column: any) => (
                            <th
                                {...column.getHeaderProps}
                                key={column.id}
                                onClick={() => { if (column.id !== 'edit') handleHeaderClick(column) }}
                                className={column.id === 'edit' ? 'noclick-column-header' : ''}
                            >
                                <div style={{ display: 'flex', flexDirection: 'row' }}>
                                {column.render('Header')}
                                <span>
                                    {orderField === column.orderField ? (orderDirection === 'ASC' ? ' ↓' : ' ↑') : ''}
                                </span>
                                </div>
                            </th>))}
                    </tr>
              )
            })}
            </thead>
            <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row)
              return (
                    <tr {...row.getRowProps()} key={'row-' + row.id}>
                        {row.cells.map((cell) => (
                            <td {...cell.getCellProps()} key={'cell -' + cell.column.id}>{cell.render('Cell')}</td>
                        ))}
                    </tr>
              )
            })}
            </tbody>
        </table>
              )}
      </React.Fragment>
  )
}
